import { Button, Dialog, Field, Input, Portal, Stack, Textarea } from '@chakra-ui/react';
import { formatLessonCodeSummary } from '@opanuj/shared/utils';
import { useEffect, useRef, useState } from 'react';

type StudentInfoModalProps = {
	pickedSlot: any | null;
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (payload: { name: string; notes: string }) => void;
};

export const StudentInfoModal = ({ pickedSlot, isOpen, onClose, onSubmit }: StudentInfoModalProps) => {
	const [knownStudents, setKnownStudents] = useState<string[]>([]);
	const [name, setName] = useState('');
	const [notes, setNotes] = useState('');
	const cancelRef = useRef<HTMLButtonElement | null>(null);
	const nameInputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (!isOpen) {
			setName('');
			setNotes('');
			return;
		}

		setNotes(pickedSlot?.notes ?? '');
		requestAnimationFrame(() => {
			nameInputRef.current?.focus();
		});
	}, [isOpen, pickedSlot]);

	if (!isOpen) {
		return null;
	}

	const handleSubmit = () => {
		const trimmedName = name.trim();
		if (!trimmedName || !pickedSlot) return;
		if (!knownStudents.includes(trimmedName)) {
			setKnownStudents((previous) => [...previous, trimmedName]);
		}
		onSubmit({ name: trimmedName, notes: notes.trim() });
	};

	return (
		<Dialog.Root
			open={isOpen}
			lazyMount
			unmountOnExit
			initialFocusEl={() => cancelRef.current}
			role="alertdialog"
			onOpenChange={(event) => {
				if (!event.open) {
					onClose();
				}
			}}
		>
			<Portal>
				<Dialog.Backdrop/>
				<Dialog.Positioner>
					<Dialog.Content>
						<Dialog.Header fontSize="lg" fontWeight="bold">
							{pickedSlot ? formatLessonCodeSummary(pickedSlot.code ?? pickedSlot.slotCode ?? '') : 'Dane ucznia/uczennicy'}
						</Dialog.Header>
						<Dialog.Body>
							<Stack>
								<Field.Root>
									<Field.Label>Imię i nazwisko ucznia/uczennicy</Field.Label>
									<Input
										ref={nameInputRef}
										list={knownStudents.length ? 'known-student-names' : undefined}
										value={name}
										onChange={(event) => setName(event.target.value)}
										onKeyDown={(event) => {
											if (event.key === 'Enter') {
												handleSubmit();
											}
										}}
									/>
									{knownStudents.length > 0 && (
										<datalist id="known-student-names">
											{knownStudents.map((knownStudent) => (
												<option key={knownStudent} value={knownStudent} />
											))}
										</datalist>
									)}
								</Field.Root>
								<Field.Root>
									<Field.Label>Uwagi do zapisu:</Field.Label>
									<Textarea
										value={notes}
										onChange={(event) => setNotes(event.target.value)}
										rows={4}
									/>
								</Field.Root>
							</Stack>
						</Dialog.Body>
						<Dialog.Footer>
							<Button ref={cancelRef} onClick={onClose} variant="ghost">
								Anuluj
							</Button>
							<Button
								colorScheme="orange"
								disabled={!name.trim() || !pickedSlot}
								onClick={handleSubmit}
								ml={3}
							>
								Wybierz
							</Button>
						</Dialog.Footer>
					</Dialog.Content>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};
